import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Link as FluentLink,
  Tooltip,
} from '@fluentui/react-components';
import {
  ChevronRight16Regular,
  Delete20Regular,
  Print20Regular,
  ArrowDownload20Regular,
  Mail20Regular,
  PanelRight20Regular,
  PanelRightContract20Regular,
  Checkmark20Regular,
} from '@fluentui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Header from '../components/Header';
import DocumentDetailsPane, { documentQueryKey, useDocumentQuery } from '../components/DocumentDetailsPane';
import {
  buildPdfOverrideParam,
  DocumentFieldOverrides,
  EditableField,
} from '../components/DocumentPreview';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import SendDocumentDialog from '../components/SendDocumentDialog';
import { archiveDocument, DocumentDetails } from '../services/documentService';
import { fetchEmailTemplates } from '../services/emailTemplateService';
import { apiFetch } from '../lib/apiFetch';
import { queryKeys } from '../lib/queryClient';
import { buildReceiptLabels } from '../lib/tonnage';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding('8px'),
    '@media print': {
      backgroundColor: '#ffffff',
      ...shorthands.padding('0'),
      display: 'block',
      height: 'auto',
    },
  },
  // Single elevated workspace card — mirrors the Orders / Customers list
  // treatment so /documents/:id reads as one continuous surface (toolbar +
  // document + side panel all inside the same raised card).
  workspaceCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    flexGrow: 1,
    // Floor to the viewport so flexGrow can't collapse the card to content
    // height when the nested flex height chain fails to resolve (gray gap
    // below the work surface on scroll). flexShrink:0 prevents re-squeeze.
    minHeight: 'calc(100vh - 64px)',
    flexShrink: 0,
    ...shorthands.overflow('hidden'),
    '@media print': {
      boxShadow: 'none',
      ...shorthands.borderRadius('0'),
    },
  },
  body: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    '@media print': {
      overflow: 'visible',
    },
    // Stack the document and side panel vertically on mobile so the
    // 300px side rail doesn't crush the doc preview.
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      overflowY: 'auto',
    },
  },
  contentArea: {
    flex: 1,
    overflowY: 'auto',
    ...shorthands.padding('32px', '24px'),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    backgroundColor: tokens.colorNeutralBackground2,
    '@media print': {
      ...shorthands.padding('0'),
      backgroundColor: '#ffffff',
      overflow: 'visible',
    },
  },
  card: {
    width: '100%',
    maxWidth: '900px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('6px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    overflow: 'hidden',
    '@media print': {
      ...shorthands.border('0'),
      maxWidth: 'none',
      ...shorthands.borderRadius('0'),
    },
  },
  rightPanel: {
    width: '300px',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderLeft('1px', 'solid', tokens.colorNeutralStroke2),
    overflowY: 'auto',
    '@media (max-width: 768px)': {
      width: '100%',
      ...shorthands.borderLeft('0'),
      ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
      overflowY: 'visible',
    },
    '@media print': {
      display: 'none',
    },
  },
  panelSection: {
    ...shorthands.padding('16px', '20px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  panelHeading: {
    fontSize: '13px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
  },
  panelPlaceholder: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

interface FromOrderState {
  id?: string;
  number?: string | number;
  // Local (Supabase) order id — distinct from the Booqable `id` above. Carried
  // so a document opened *via its order* can pull that order's landfill
  // (tonnage) receipts into the Send-Email attachment list. Absent when the
  // document is opened from the Documents list or a project.
  localId?: string | null;
}

// Wrap a disabled-for-cause button in a Tooltip explaining the reason. With no
// reason the button renders bare — avoids an empty-content Tooltip clobbering
// the button's own accessible label.
function withFinalizeTooltip(reason: string | null, button: React.ReactElement) {
  if (!reason) return button;
  return (
    <Tooltip content={reason} relationship="label">
      {button}
    </Tooltip>
  );
}

const DocumentEdit: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();
  const location = useLocation();
  const fromOrder = (location.state as { fromOrder?: FromOrderState } | null)?.fromOrder;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const queryClient = useQueryClient();

  // Inline edits from the preview (customer block / number / date). Applied as
  // one-off overrides on download — not persisted to the source (see GH #519).
  const [overrides, setOverrides] = useState<DocumentFieldOverrides>({});
  const handleFieldChange = useCallback((field: EditableField, value: string) => {
    setOverrides((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleDownloadPdf = async () => {
    if (!documentId || downloading) return;
    setDownloading(true);
    try {
      // react-pdf renderer (branded, day-1 primary) — also the only path that
      // honors ?overrides=. Merge the preview edits in when present.
      const base = `/documents/${encodeURIComponent(documentId)}/pdf?renderer=react`;
      const ov = buildPdfOverrideParam(overrides);
      const res = await apiFetch(ov ? `${base}&overrides=${ov}` : base);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Draft invoices have no number yet — name the file "Draft invoice"
      // instead of falling back to the bare document UUID.
      a.download = isProFormaInvoice
        ? 'Draft invoice.pdf'
        : `Invoice ${attrs?.number || documentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
      alert('Could not download PDF. Check the backend logs.');
    } finally {
      setDownloading(false);
    }
  };

  // Shares the fetch with DocumentDetailsPane via the same query key — one
  // network roundtrip backs both the doc body and our toolbar decisions.
  const { data: documentData } = useDocumentQuery(documentId);
  const attrs = documentData?.data?.attributes as
    | { document_type?: string; finalized?: boolean; number?: string | null }
    | undefined;
  const isProFormaInvoice =
    (attrs?.document_type ?? 'invoice') === 'invoice' && attrs?.finalized === false;

  // Finalizing mints the official invoice number, so it's only allowed once the
  // order is reserved (or further along). Disable the button — with a reason —
  // rather than letting staff open the dialog and hit the backend's 409. Mirror
  // the backend's fail-open: a *known* pre-reservation status blocks; an unknown
  // status (still loading, or absent) leaves the button enabled and the backend
  // guard as the backstop. Keep this list in sync with app.py's
  // _FINALIZE_ALLOWED_ORDER_STATUSES.
  const orderStatus = (documentData as any)?.processed?.order?.status as string | undefined;
  const finalizeStatusBlocked =
    orderStatus && !['reserved', 'started', 'stopped'].includes(orderStatus)
      ? `The order must be reserved before its invoice can be finalized (order status: ${orderStatus}).`
      : null;

  // Order id off the document's relationships — used to pull sibling docs
  // (other invoices/quotes for the same order) for the dialog's attachment
  // list. Falls back to fromOrder state when present.
  const docOrderId =
    (documentData as any)?.data?.relationships?.order?.data?.id ?? fromOrder?.id ?? null;

  // Warm the Send-Email template cache as soon as the order id is known, so the
  // dialog's default "Invoice send" body renders instantly on open instead of a
  // beat later. Resolving the template variables server-side (order + invoice +
  // pay link) is a multi-round-trip ~2s call; prefetching it here (same query
  // key the dialog reads) moves that cost off the open→populate path. Sending an
  // invoice is the likely next action from this editor, so the fetch isn't waste.
  useEffect(() => {
    if (!docOrderId) return;
    queryClient.prefetchQuery({
      queryKey: queryKeys.emailTemplates.list(docOrderId),
      queryFn: () => fetchEmailTemplates(docOrderId),
    });
  }, [docOrderId, queryClient]);

  // Pull the order to learn whether its customer requires a PO and the current
  // PO number, so Finalize can be pre-disabled (with a reason) when a required
  // PO is missing. The backend finalize guard is the backstop; this is just the
  // UX affordance. customer_po_required + po_number are TWM overlays on
  // /booqable/orders/<id> (Booqable stores neither).
  const { data: poOrderData } = useQuery<any>({
    queryKey: ['order-po-guard', docOrderId],
    queryFn: () =>
      apiFetch(`/booqable/orders/${encodeURIComponent(docOrderId!)}`).then((r) => r.json()),
    enabled: !!docOrderId,
  });
  const poOrderAttrs = poOrderData?.data?.attributes;
  const poRequiredMissing =
    !!poOrderAttrs?.customer_po_required && !String(poOrderAttrs?.po_number ?? '').trim();

  // Finalize is blocked when the order isn't reserved yet OR the customer
  // requires a PO the order doesn't have. Status takes precedence in the copy.
  const finalizeBlockedReason =
    finalizeStatusBlocked ??
    (poRequiredMissing
      ? 'This customer requires a PO number. Enter it on the order before finalizing the invoice.'
      : null);

  // Persist the invoice payment-terms selection onto the order (unlike the
  // inline customer edits, which are one-off PDF overrides). Refetch the
  // document so the preview — and the next downloaded/emailed PDF, both of
  // which read the canonical backend data — reflect the new term.
  const handlePaymentTermsChange = useCallback(
    async (termKey: string) => {
      if (!docOrderId) return;
      try {
        const res = await apiFetch(`/booqable/orders/${encodeURIComponent(docOrderId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payment_terms: termKey }),
        });
        if (!res.ok) throw new Error(`Payment-terms update failed (${res.status})`);
        if (documentId) {
          queryClient.invalidateQueries({ queryKey: documentQueryKey(documentId) });
        }
      } catch (err) {
        console.error('[payment-terms] update failed:', err);
      }
    },
    [docOrderId, documentId, queryClient],
  );

  // Reuse the sidebar's order-documents query so the attachment list lights up
  // instantly when navigating from the order page; falls back to a fresh fetch
  // when we landed here from the Documents list.
  const { data: orderDocsData } = useQuery<{
    data: Array<{
      id: string;
      document_type: string;
      number: string | null;
      grand_total_in_cents: number | null;
    }>;
  }>({
    queryKey: ['order-documents', docOrderId],
    queryFn: async () => {
      const res = await apiFetch(`/booqable/orders/${encodeURIComponent(docOrderId!)}/documents`);
      if (!res.ok) throw new Error(`order-documents fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!docOrderId,
    staleTime: 30 * 1000,
  });

  // Landfill (tonnage) receipts for the order — only fetched when we arrived
  // *via the order* (fromOrder carries its local id). Same query key as the
  // order sidebar's receipt query, so the cache is warm when navigating from
  // the order page. The Documents-list / project paths don't fetch these: a
  // document opened on its own sends only itself.
  const { data: receiptAttData } = useQuery<{
    data: Array<{
      id: string;
      name: string;
      line_id: string | null;
      line_label: string | null;
      created_at: string | null;
    }>;
  }>({
    queryKey: ['order-attachments', fromOrder?.localId, 'tonnage_receipt', null],
    queryFn: async () => {
      const res = await apiFetch(
        `/orders/${encodeURIComponent(fromOrder!.localId!)}/attachments?kind=tonnage_receipt`,
      );
      if (!res.ok) throw new Error(`order-attachments fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!fromOrder?.localId,
    staleTime: 30 * 1000,
  });
  const receiptAttachments = receiptAttData?.data ?? [];

  // SendDocumentDialog reads id, number, customerEmail, and orderDocuments.
  // Built from cached payloads — no slow multi-roundtrip documentService call.
  const sendDocumentDetails = React.useMemo<DocumentDetails | null>(() => {
    if (!documentId || !documentData) return null;
    const customer = (documentData as any).processed?.customer ?? {};
    // Booqable's grand_total_in_cents is EXCL. taxes; grand_total_with_tax_in_cents
    // is INCL. The send dialog shows the amount owed, so prefer the with-tax total
    // (the order-documents list endpoint already aliases it into grand_total_in_cents;
    // this fallback reads the raw single-doc payload, which keeps both fields).
    const grandTotalCents =
      (attrs as any)?.grand_total_with_tax_in_cents ?? (attrs as any)?.grand_total_in_cents ?? 0;
    const date = (attrs as any)?.date ?? (attrs as any)?.created_at ?? '';

    // The single current document, used on its own when the dialog should send
    // just this doc.
    const thisDoc = {
      id: documentId,
      type: attrs?.document_type ?? 'invoice',
      number: attrs?.number ?? 'Draft invoice',
      amount: grandTotalCents / 100,
      date,
    };

    // Attachment list depends on how the document was opened:
    //  • Via its order (fromOrder): the email represents the whole order, so
    //    offer every order doc PLUS the order's landfill (tonnage) receipts as
    //    toggleable attachments.
    //  • Directly (Documents list / project): send only THIS document.
    let orderDocuments: DocumentDetails['orderDocuments'];
    if (fromOrder) {
      const siblings = orderDocsData?.data ?? [];
      orderDocuments = siblings.length
        ? siblings.map((d) => ({
            id: d.id,
            type: d.document_type,
            number: d.number ?? 'Draft invoice',
            amount: (d.grand_total_in_cents ?? 0) / 100,
            date,
          }))
        : [thisDoc];
      // Landfill receipts: one synthetic, individually-selectable attachment per
      // tonnage_receipt scan, keyed `landfill-receipt-att-<id>` (the id form
      // /emails/send resolves to the stored receipt image) and labeled by its
      // weighed line. Mirrors the order sidebar's Send dialog.
      if (receiptAttachments.length) {
        const labels = buildReceiptLabels(receiptAttachments, null);
        for (const r of receiptAttachments) {
          orderDocuments.push({
            id: `landfill-receipt-att-${r.id}`,
            type: 'landfill_receipt',
            number: labels.get(r.id) || r.line_label || r.name || 'Receipt',
            amount: 0,
            date: '',
          });
        }
      }
    } else {
      orderDocuments = [thisDoc];
    }

    return {
      id: documentId,
      number: attrs?.number ?? '',
      type: attrs?.document_type ?? 'invoice',
      status: '',
      exclTaxes: 0,
      inclTaxes: grandTotalCents / 100,
      customerName: customer.name ?? '',
      // Seed for the Send dialog's To:. Prefer the backend-resolved billing
      // contact (AP mail for invoices) over the company's general email; the
      // admin can still edit the chip before sending.
      customerEmail: customer.billing_email || customer.email || '',
      // Booqable order id — lets the Send-Email dialog fetch templates rendered
      // against this order's real values (instead of raw {token} placeholders).
      orderId: docOrderId ?? undefined,
      orderDocuments,
    };
  }, [documentId, documentData, attrs, orderDocsData, docOrderId, fromOrder, receiptAttachments]);

  const handleFinalize = async () => {
    if (!documentId) return;
    setFinalizing(true);
    try {
      const res = await apiFetch(`/booqable/documents/${documentId}/finalize`, {
        method: 'POST',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        console.error('[finalize-invoice] failed:', res.status, body);
        return;
      }
      // Refetch the doc so the button hides and the invoice number appears.
      queryClient.invalidateQueries({ queryKey: documentQueryKey(documentId) });
      // Also refresh the originating order's sidebar so navigating back shows
      // the now-finalized invoice (with its number) instead of "Pro forma".
      queryClient.invalidateQueries({ queryKey: ['order-documents'] });
      setFinalizeDialogOpen(false);
    } catch (err) {
      console.error('[finalize-invoice] error:', err);
    } finally {
      setFinalizing(false);
    }
  };

  const handleDelete = async () => {
    if (!documentId) return;
    try {
      await archiveDocument(documentId);
      // Drop the deleted doc from the order's sidebar list so navigating back
      // doesn't show a stale entry.
      queryClient.invalidateQueries({ queryKey: ['order-documents'] });
      if (fromOrder?.id) {
        navigate(`/orders/${fromOrder.id}`);
      } else {
        navigate('/documents');
      }
    } catch (err) {
      console.error('Error archiving document:', err);
      alert('Could not delete this document. Check the backend logs.');
    }
  };

  const goBack = () => {
    if (fromOrder?.id) {
      navigate(`/orders/${fromOrder.id}`);
    } else {
      navigate('/documents');
    }
  };

  const crumbStyle: React.CSSProperties = {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
  };

  return (
    <div className={styles.page}>
      <div className={styles.workspaceCard}>
      <Header
        inline
        customTitle={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {fromOrder?.number !== undefined ? (
              <FluentLink
                onClick={(e) => { e.preventDefault(); goBack(); }}
                href={fromOrder.id ? `/orders/${fromOrder.id}` : '/orders'}
                style={crumbStyle}
              >
                Order #{fromOrder.number}
              </FluentLink>
            ) : (
              <FluentLink
                onClick={(e) => { e.preventDefault(); navigate('/documents'); }}
                href="/documents"
                style={crumbStyle}
              >
                Documents
              </FluentLink>
            )}
            <ChevronRight16Regular style={{ color: tokens.colorNeutralForeground3 }} />
            <span style={crumbStyle}>Invoice</span>
          </div>
        }
        customActions={
          <>
            {/* Delete (archive) is hidden for pro forma invoices: a pro forma
                is a live projection of the order's balance, so Booqable just
                regenerates a fresh one (new UUID) in its place — the button
                can never actually remove it. Editing the order or cancelling it
                is the real intent. Delete stays for finalized invoices, quotes,
                and contracts, where archiving is meaningful. */}
            {!isProFormaInvoice && (
              <Button
                appearance="subtle"
                icon={<Delete20Regular />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>
            )}
            <Button
              appearance="subtle"
              icon={<Print20Regular />}
              onClick={() => window.print()}
            >
              Print
            </Button>
            <Button
              appearance="subtle"
              icon={<ArrowDownload20Regular />}
              onClick={handleDownloadPdf}
              disabled={downloading}
            >
              {downloading ? 'Downloading…' : 'Download'}
            </Button>
            {isProFormaInvoice ? (
              // disabledFocusable (not plain disabled) when blocked, so the
              // button stays hoverable and the Tooltip can explain *why*; the
              // Tooltip only wraps when there's actually a reason.
              withFinalizeTooltip(
                finalizeBlockedReason,
                <Button
                  appearance="primary"
                  icon={<Checkmark20Regular />}
                  onClick={() => setFinalizeDialogOpen(true)}
                  disabled={finalizing}
                  disabledFocusable={!!finalizeBlockedReason}
                >
                  {finalizing ? 'Finalizing…' : 'Finalize'}
                </Button>,
              )
            ) : (
              <Button
                appearance="primary"
                icon={<Mail20Regular />}
                onClick={() => setSendDialogOpen(true)}
                disabled={!sendDocumentDetails}
              >
                Send
              </Button>
            )}
            <Button
              appearance="subtle"
              icon={rightPanelOpen ? <PanelRightContract20Regular /> : <PanelRight20Regular />}
              onClick={() => setRightPanelOpen((open) => !open)}
              aria-label={rightPanelOpen ? 'Close panel' : 'Open panel'}
            />
          </>
        }
      />

      <div className={styles.body}>
        <div className={styles.contentArea}>
          <div className={styles.card} data-print-root>
            {documentId && (
              <DocumentDetailsPane
                documentId={documentId}
                // Every doc type — pro forma included — is editable in the
                // preview; edits apply as one-off PDF overrides on Download
                // (not persisted to the source). Pro formas were previously
                // gated out; they're now editable like finalized invoices/quotes.
                editable
                onFieldChange={handleFieldChange}
                onPaymentTermsChange={handlePaymentTermsChange}
              />
            )}
          </div>
        </div>

        {rightPanelOpen && (
          <aside className={styles.rightPanel}>
            <div className={styles.panelSection}>
              <div className={styles.panelHeading}>Tags</div>
              <div className={styles.panelPlaceholder}>No tags yet.</div>
            </div>
            <div className={styles.panelSection}>
              <div className={styles.panelHeading}>Notes</div>
              <div className={styles.panelPlaceholder}>No notes yet.</div>
            </div>
          </aside>
        )}
      </div>

      </div>

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete document"
        message="Are you sure you want to delete this document?"
        subtext="The document will be archived and removed from active lists. This action cannot be undone."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        variant="danger"
      />

      <ConfirmationDialog
        isOpen={finalizeDialogOpen}
        onClose={() => setFinalizeDialogOpen(false)}
        onConfirm={handleFinalize}
        title="Finalize invoice"
        message="Finalize this draft invoice?"
        subtext="The invoice will be assigned its official number and become read-only. Further changes to the order will create a new prorated invoice."
        confirmButtonText="Finalize"
        cancelButtonText="Cancel"
      />

      <SendDocumentDialog
        isOpen={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        onUndo={() => setSendDialogOpen(true)}
        document={sendDocumentDetails}
      />
    </div>
  );
};

export default DocumentEdit;
